import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Comment.css';

const Comment = ({ newsId, onClose }) => {
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (newsId) {
            console.log("Fetching comments for newsId:", newsId);
            fetchComments();
        }
    }, [newsId]);

    const fetchComments = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/comments/${newsId}`
            );
            console.log("Fetched comments:", res.data);
            setComments(res.data.data.comments || []);
        } catch (err) {
            console.error("Error fetching comments:", err);
            setComments([]);
        } finally {
            setLoading(false);
        }
    };

    const submitComment = async () => {
        // Validation
        if (!name.trim() || !message.trim()) {
            alert('Name and Message are required!');
            return;
        }

        if (name.trim().length > 50) {
            alert('Name must be less than 50 characters');
            return;
        }

        if (message.trim().length > 500) {
            alert('Message must be less than 500 characters');
            return;
        }

        setSubmitting(true);
        try {
            console.log("Submitting comment:", { newsId, name, message });
            const res = await axios.post(
                `${process.env.REACT_APP_BASE_URL}api/v1/users/comment/${newsId}`,
                {
                    name: name.trim(),
                    message: message.trim()
                }
            );
            console.log("Comment submitted successfully:", res.data);
            
            // Clear form
            setName('');
            setMessage('');
            
            // Refresh comments
            fetchComments();
            
            // Show success message
            alert('Comment posted successfully!');
        } catch (err) {
            console.error("Error submitting comment:", err);
            alert('Failed to submit comment. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            submitComment();
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h3 className="modal-title">💬 Comments</h3>
                    <button
                        className="modal-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Comment Form */}
                <div className="comment-form">
                    <input
                        type="text"
                        className="comment-input"
                        placeholder="Your Name (max 50 chars)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={50}
                    />

                    <textarea
                        className="comment-textarea"
                        placeholder="Your Comment (max 500 chars) - Press Ctrl+Enter to submit"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        maxLength={500}
                        rows={4}
                    />

                    <div className="comment-form-footer">
                        <span className="char-counter">
                            {message.length}/500 characters
                        </span>
                        <button
                            className="submit-btn"
                            onClick={submitComment}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner"></span>
                                    Submitting...
                                </>
                            ) : (
                                '📤 Submit Comment'
                            )}
                        </button>
                    </div>
                </div>

                <hr className="divider" />

                {/* Comments List */}
                <div className="comments-list">
                    <h4 className="comments-list-title">
                        All Comments ({comments.length})
                    </h4>
                    
                    {loading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading comments...</p>
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="empty-state">
                            <p>📭 No comments yet.</p>
                            <p>Be the first to comment!</p>
                        </div>
                    ) : (
                        <div className="comments-container">
                            {comments.map((comment, index) => (
                                <div key={index} className="comment-item">
                                    <div className="comment-header">
                                        <div className="comment-author">
                                            <span className="author-avatar">
                                                {comment.name.charAt(0).toUpperCase()}
                                            </span>
                                            <strong>{comment.name}</strong>
                                        </div>
                                        <span className="comment-date">
                                            {new Date(comment.date).toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </span>
                                    </div>
                                    <p className="comment-message">{comment.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Comment;